/**
 * <s-header-autohide> — Desktop only (≥ 1024px): the sticky header (its closest <header>) slides up
 * out of view while the visitor scrolls down and glides back as soon as they scroll up.
 *
 * - Hides only after a long continuous downward scroll (data-threshold, default 200px) plus a
 *   short delay (data-hide-delay, 700ms); returns after 40px of upward scroll (data-show-threshold).
 *   Direction changes reset the count, so small or accidental movements never toggle it.
 * - Always visible near the top of the page (first 400px, data-top-zone), while focus is inside the header
 *   (keyboard users), and while a dialog opened from it (search, cart) is open.
 * - Phones/tablets: does nothing, so the mobile island stays visible at all times.
 * State: [data-hidden] on the header; CSS (config/styles.css › Header island) owns the motion.
 * Also marks the header [data-autohide] while active so the homepage stack can hold cards at the
 * very top instead of under a header that may be hidden.
 */
class SHeaderAutohide extends HTMLElement {
  #header = null;
  #media = window.matchMedia('(min-width: 64rem)');
  #lastY = 0;
  #travel = 0;
  #frame = 0;
  #onScroll = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };
  #onMedia = () => this.#sync();
  #onFocusIn = () => this.#show();

  connectedCallback() {
    this.#header = this.closest('header');
    if (!this.#header) return;
    this.#media.addEventListener('change', this.#onMedia);
    this.#header.addEventListener('focusin', this.#onFocusIn);
    this.#sync();
  }

  disconnectedCallback() {
    this.#cancelHide();
    this.#media.removeEventListener('change', this.#onMedia);
    this.#header?.removeEventListener('focusin', this.#onFocusIn);
    window.removeEventListener('scroll', this.#onScroll);
    cancelAnimationFrame(this.#frame);
  }

  #sync() {
    const active = this.#media.matches;
    this.#header.toggleAttribute('data-autohide', active);
    this.#lastY = window.scrollY;
    this.#travel = 0;
    this.#show();
    if (active) window.addEventListener('scroll', this.#onScroll, { passive: true });
    else window.removeEventListener('scroll', this.#onScroll);
  }

  #show() {
    if (this.#hideTimer) this.#cancelHide();
    this.#header?.removeAttribute('data-hidden');
  }

  #hideTimer = 0;

  #cancelHide() {
    clearTimeout(this.#hideTimer);
    this.#hideTimer = 0;
  }

  #update() {
    const y = Math.max(0, window.scrollY);
    const delta = y - this.#lastY;
    this.#lastY = y;
    if (delta === 0) return;

    // Keep the header while a dialog is open or focus is inside it, and near the top of the page.
    const keep = document.querySelector('dialog[open]') || this.#header.querySelector(':focus-visible');
    if (keep || y < Number(this.dataset.topZone || 400)) {
      this.#travel = 0;
      this.#cancelHide();
      this.#show();
      return;
    }

    // Accumulate travel in one direction; a reversal starts a new count.
    this.#travel = Math.sign(delta) === Math.sign(this.#travel) ? this.#travel + delta : delta;
    const hideAfter = Number(this.dataset.threshold || 200); // px of continuous downward scroll
    const showAfter = Number(this.dataset.showThreshold || 40); // px of intentional upward scroll

    if (this.#travel > hideAfter) {
      // Stay available a little longer, then hide (cancelled by any upward scroll meanwhile).
      if (!this.#hideTimer && !this.#header.hasAttribute('data-hidden')) {
        this.#hideTimer = setTimeout(() => {
          this.#hideTimer = 0;
          if (this.#travel > 0 && !document.querySelector('dialog[open]')) {
            this.#header.setAttribute('data-hidden', '');
          }
        }, Number(this.dataset.hideDelay || 700));
      }
    } else if (this.#travel < -showAfter) {
      this.#cancelHide();
      this.#show();
    }
  }
}

if (!customElements.get('s-header-autohide')) customElements.define('s-header-autohide', SHeaderAutohide);
